import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { StickyNote, Hash, Users } from "lucide-react";
import { useAuth } from "../scripts/AuthContext";
import {
  ensureDmNotes,
  getOrCreateDm,
  listDmConversations,
  listMyChannels,
  sendDmMessage,
} from "@/lib/dmApi";
import { postAttachment } from "@/lib/permalinks";
import { toastError, toastSuccess } from "@/lib/toast";
import { markdownToPlain } from "@/lib/markdownPlain";
import { getFullName } from "@/lib/userUtils";
import { API_BASE_URL } from "@/lib/apiBase";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Instagram-style “Send to…” picker: You, people, groups, and channels.
 * Closes after send without opening the chat.
 */
export default function SharePostToDmModal({
  open,
  onOpenChange,
  orgId,
  post,
}) {
  const { accessToken } = useAuth();
  const [memberQuery, setMemberQuery] = useState("");
  const [members, setMembers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [channels, setChannels] = useState([]);
  const [youConversation, setYouConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  const [sendingTo, setSendingTo] = useState(null);

  const postId = post?.post_id ?? post?.postId;
  const title = markdownToPlain(post?.title || "").trim();
  const isCard = !!(post?.kanbanStatus || post?.kanban_status);

  const currentUserId = useMemo(() => {
    try {
      return accessToken ? jwtDecode(accessToken)?.sub : null;
    } catch {
      return null;
    }
  }, [accessToken]);

  useEffect(() => {
    if (!open) {
      setMemberQuery("");
      setNote("");
      setSendingTo(null);
      setYouConversation(null);
      return;
    }
    if (!accessToken || !orgId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [membersRes, conversations, myChannels, notes] =
          await Promise.all([
            axios.get(`${API_BASE_URL}/org/${orgId}/members`, {
              headers: { Authorization: `Bearer ${accessToken}` },
            }),
            listDmConversations(accessToken, orgId),
            listMyChannels(accessToken, orgId),
            ensureDmNotes(accessToken, orgId).catch(() => null),
          ]);
        if (cancelled) return;
        setMembers(membersRes.data || []);
        const convList = conversations || [];
        setGroups(
          convList.filter((c) => c.type === "group" && !c.is_self)
        );
        const selfFromList = convList.find((c) => c.is_self);
        setYouConversation(selfFromList || notes || null);
        const fromList = convList.filter((c) => c.type === "channel");
        const channelMap = new Map();
        for (const c of [...fromList, ...(myChannels || [])]) {
          const id = c.conversation_id ?? c.conversationId;
          if (id != null) channelMap.set(Number(id), c);
        }
        setChannels([...channelMap.values()]);
      } catch (err) {
        console.error("Failed to load share targets:", err);
        if (!cancelled) {
          setMembers([]);
          setGroups([]);
          setChannels([]);
          setYouConversation(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, accessToken, orgId]);

  const youItem = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    const matchesYou =
      !q ||
      "you".includes(q) ||
      "notes".includes(q) ||
      q.includes("you") ||
      q.includes("notes");
    if (!matchesYou) return null;
    return {
      kind: "you",
      key: `s-${youConversation?.conversation_id ?? "you"}`,
      conversation: youConversation,
    };
  }, [memberQuery, youConversation]);

  const filteredPeople = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    return (members || [])
      .map((m) => {
        const u = m.user || m;
        if (!u?.user_id) return null;
        return {
          ...u,
          org_email: m.org_email ?? u.org_email ?? null,
          title: m.title ?? u.title ?? null,
        };
      })
      .filter(Boolean)
      .filter((u) => u.user_id !== currentUserId)
      .filter((u) => {
        if (!q) return true;
        const name = `${u.first_name || ""} ${u.last_name || ""}`
          .trim()
          .toLowerCase();
        return (
          name.includes(q) ||
          (u.org_email || "").toLowerCase().includes(q) ||
          (u.title || "").toLowerCase().includes(q)
        );
      })
      .map((u) => ({ kind: "person", key: `p-${u.user_id}`, user: u }));
  }, [members, memberQuery, currentUserId]);

  const filteredGroups = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    return (groups || [])
      .filter((c) => {
        if (!q) return true;
        return (c.title || "Group").toLowerCase().includes(q);
      })
      .map((c) => ({
        kind: "group",
        key: `g-${c.conversation_id}`,
        conversation: c,
      }));
  }, [groups, memberQuery]);

  const filteredChannels = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    return (channels || [])
      .filter((c) => {
        if (!q) return true;
        return (c.title || "Channel").toLowerCase().includes(q);
      })
      .map((c) => ({
        kind: "channel",
        key: `c-${c.conversation_id}`,
        conversation: c,
      }));
  }, [channels, memberQuery]);

  const results = useMemo(() => {
    const rest = [...filteredChannels, ...filteredGroups, ...filteredPeople];
    return youItem ? [youItem, ...rest] : rest;
  }, [youItem, filteredChannels, filteredGroups, filteredPeople]);

  const buildAttachment = () =>
    postAttachment({
      orgId,
      postId,
      title: title || undefined,
      kind: isCard ? "card" : "post",
    });

  const sendToPerson = async (user) => {
    if (!user?.user_id || !accessToken || !orgId || postId == null) return;
    setSendingTo(`p-${user.user_id}`);
    try {
      const conv = await getOrCreateDm(accessToken, orgId, user.user_id);
      await sendDmMessage(accessToken, orgId, conv.conversation_id, {
        content: (note || "").trim(),
        attachment: buildAttachment(),
      });
      toastSuccess(isCard ? "Card sent" : "Post sent");
      onOpenChange?.(false);
    } catch (err) {
      console.error("Failed to send post in DM:", err);
      toastError(err, "Could not send post");
    } finally {
      setSendingTo(null);
    }
  };

  const sendToConversation = async (conversation, kind) => {
    const cid = conversation?.conversation_id;
    if (!cid || !accessToken || !orgId || postId == null) return;
    const prefix =
      kind === "channel" ? "c" : kind === "you" ? "s" : "g";
    setSendingTo(`${prefix}-${cid}`);
    try {
      await sendDmMessage(accessToken, orgId, cid, {
        content: (note || "").trim(),
        attachment: buildAttachment(),
      });
      toastSuccess(isCard ? "Card sent" : "Post sent");
      onOpenChange?.(false);
    } catch (err) {
      console.error(`Failed to send post to ${kind}:`, err);
      toastError(err, "Could not send post");
    } finally {
      setSendingTo(null);
    }
  };

  const sendToYou = async () => {
    if (!accessToken || !orgId || postId == null) return;
    setSendingTo(youItem?.key || "s-you");
    try {
      let conv = youConversation;
      if (!conv?.conversation_id) {
        conv = await ensureDmNotes(accessToken, orgId);
        setYouConversation(conv);
      }
      const cid = conv?.conversation_id;
      if (!cid) throw new Error("Could not open You conversation");
      await sendDmMessage(accessToken, orgId, cid, {
        content: (note || "").trim(),
        attachment: buildAttachment(),
      });
      toastSuccess(isCard ? "Card sent" : "Post sent");
      onOpenChange?.(false);
    } catch (err) {
      console.error("Failed to send post to You:", err);
      toastError(err, "Could not send post");
    } finally {
      setSendingTo(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Send to…</DialogTitle>
          <DialogDescription>
            Share this {isCard ? "card" : "post"} with yourself, a person,
            group, or channel.
          </DialogDescription>
        </DialogHeader>
        {(title || postId != null) && (
          <p className="text-sm text-muted-foreground line-clamp-2 border rounded-md px-3 py-2 bg-muted/30">
            {title || `${isCard ? "Card" : "Post"} #${postId}`}
          </p>
        )}
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a message (optional)"
        />
        <Input
          value={memberQuery}
          onChange={(e) => setMemberQuery(e.target.value)}
          placeholder="Search people, groups, or channels…"
          autoFocus
        />
        <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
          {loading ? (
            <p className="p-3 text-sm text-muted-foreground">Loading…</p>
          ) : results.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">No matches</p>
          ) : (
            results.map((item) => {
              if (item.kind === "you") {
                return (
                  <button
                    key={item.key}
                    type="button"
                    disabled={!!sendingTo}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-muted/60 disabled:opacity-50 flex items-center justify-between gap-2"
                    onClick={sendToYou}
                  >
                    <span className="min-w-0 flex items-center gap-2 truncate">
                      <StickyNote className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="font-medium truncate">You</span>
                      <span className="text-xs text-muted-foreground truncate">
                        Notes
                      </span>
                    </span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {sendingTo === item.key || sendingTo === "s-you"
                        ? "Sending…"
                        : "Send"}
                    </span>
                  </button>
                );
              }
              if (item.kind === "channel") {
                const c = item.conversation;
                return (
                  <button
                    key={item.key}
                    type="button"
                    disabled={!!sendingTo}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-muted/60 disabled:opacity-50 flex items-center justify-between gap-2"
                    onClick={() => sendToConversation(c, "channel")}
                  >
                    <span className="min-w-0 flex items-center gap-2 truncate">
                      <Hash className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="font-medium truncate">
                        {c.title || "Channel"}
                      </span>
                    </span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {sendingTo === item.key ? "Sending…" : "Send"}
                    </span>
                  </button>
                );
              }
              if (item.kind === "group") {
                const c = item.conversation;
                return (
                  <button
                    key={item.key}
                    type="button"
                    disabled={!!sendingTo}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-muted/60 disabled:opacity-50 flex items-center justify-between gap-2"
                    onClick={() => sendToConversation(c, "group")}
                  >
                    <span className="min-w-0 flex items-center gap-2 truncate">
                      <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="font-medium truncate">
                        {c.title || "Group"}
                      </span>
                    </span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {sendingTo === item.key ? "Sending…" : "Send"}
                    </span>
                  </button>
                );
              }
              const u = item.user;
              return (
                <button
                  key={item.key}
                  type="button"
                  disabled={!!sendingTo}
                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-muted/60 disabled:opacity-50 flex items-center justify-between gap-2"
                  onClick={() => sendToPerson(u)}
                >
                  <span className="min-w-0 flex items-center gap-2 truncate">
                    <OrgMemberAvatar
                      user={u}
                      className="h-7 w-7"
                      fallbackClassName="text-[10px]"
                    />
                    <span className="font-medium truncate">{getFullName(u)}</span>
                  </span>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {sendingTo === item.key ? "Sending…" : "Send"}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
